import Button from '@mui/material/Button';
import { FormattedMessage } from 'react-intl';
import Spacings from '~/modules/ui-kit/spacings';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { z } from 'zod';
import { useAppForm } from '~/components/Form';
import messages from './messages';

type TurnReportAttendeesProps = {
  turnReport: {
    closed: boolean;
    attendees?: Array<{
      attendance?: string | null;
    }> | null;
    id: number;
    turn: {
      id: number;
      turnAssignments: {
        publisherMiniRecord: {
          firstName: string;
          lastName: string;
        };
      }[];
    };
  };
  onSubmit: (
    data: Omit<TurnReportAttendeesProps['turnReport'], 'turn'> & {
      turn: number;
    }
  ) => void;
};

const formInputsSchema = z.object({
  closed: z.boolean(),
  attendees: z.array(
    z
      .object({
        attendance: z.boolean(),
      })
      .passthrough()
  ),
});

type IFormInputs = z.infer<typeof formInputsSchema>;

const TurnReportAttendees = (props: TurnReportAttendeesProps) => {
  const defaultValues: IFormInputs = {
    closed: props.turnReport.closed,
    attendees:
      props.turnReport.attendees?.map((attendee) => ({
        ...attendee,
        attendance: attendee.attendance === 'Attendant',
      })) || [],
  };
  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: formInputsSchema,
    },
    listeners: {
      onChange: ({ formApi }) => {
        if (formApi.state.isValid) {
          formApi.handleSubmit();
        }
      },
      onChangeDebounceMs: 500,
    },
    onSubmit: ({ value }) => {
      props.onSubmit({
        ...props.turnReport,
        turn: props.turnReport.turn.id,
        closed: value.closed || props.turnReport.closed,
        attendees: value.attendees.map((attendee) => ({
          ...attendee,
          attendance: attendee.attendance ? 'Attendant' : 'Absent',
        })),
      });
    },
  });

  const handleCloseReport = () => {
    form.setFieldValue('closed', true);
    form.handleSubmit();
  };
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <Spacings.Inset>
        <Spacings.Stack scale="m">
          <Spacings.Stack scale="s">
            <div>
              <Typography variant="subtitle2" color="GrayText">
                <FormattedMessage {...messages.status} />
              </Typography>
              <Typography variant="h6">
                {props.turnReport.closed ? (
                  <FormattedMessage {...messages.statusClosed} />
                ) : (
                  <FormattedMessage {...messages.statusOpen} />
                )}
              </Typography>
            </div>
            {!props.turnReport.closed ? (
              <div>
                <Button
                  onClick={handleCloseReport}
                  size="large"
                  variant="outlined"
                >
                  <FormattedMessage {...messages.closeReport} />
                </Button>
              </div>
            ) : null}
          </Spacings.Stack>
          <Divider />
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              <FormLabel component="legend">
                <FormattedMessage {...messages.attendees} />
              </FormLabel>
              {props.turnReport.attendees?.map((_, index) => (
                <form.AppField
                  key={index}
                  name={`attendees[${index}].attendance`}
                >
                  {(field) => (
                    <field.CheckboxField
                      label={`${props.turnReport.turn.turnAssignments[index].publisherMiniRecord.firstName} ${props.turnReport.turn.turnAssignments[index].publisherMiniRecord.lastName}`}
                      disabled={props.turnReport.closed}
                    />
                  )}
                </form.AppField>
              ))}
            </FormGroup>
          </FormControl>
        </Spacings.Stack>
      </Spacings.Inset>
    </form>
  );
};

export default TurnReportAttendees;
